/**
 * Context-based hooks for lesson data access
 *
 * These hooks provide access to lesson data from StudentDataContext.
 * Lesson selection is derived from URL parameters (lessonSlug, lessonId).
 */

import { useStudentData } from "../StudentDataContext";

/**
 * Hook to access lesson state from context
 *
 * Note: selectedLesson and selectedSubject are derived from URL parameters.
 * The context observes lessonSlug/lessonId from the URL and resolves
 * the matching lesson from currentWeekSubjects automatically.
 */
export function useLessonSelector() {
  const {
    selectedLesson,
    selectedSubject,
    showBlockNavigationModal,
    pendingNavigationPath,
    hasAttemptedApplyQuestion,
    isApplyResultViewShown,
    isReviewContentSidebarOpen
  } = useStudentData();

  return {
    selectedLesson,
    selectedSubject,
    showBlockNavigationModal,
    pendingNavigationPath,
    hasAttemptedApplyQuestion,
    isApplyResultViewShown,
    isReviewContentSidebarOpen
  };
}

/**
 * Hook to access lesson actions from context
 *
 * Note: Lesson/subject selection is derived from URL parameters.
 * Navigation via react-router's Link or navigate() is the source of truth.
 */
export function useLessonDispatch() {
  const {
    fetchSectionContent,
    startLesson,
    completeLesson,
    setShowBlockNavigationModal,
    setPendingNavigationPath,
    setHasAttemptedApplyQuestion,
    setApplyResultViewShown,
    openReviewContentSidebar,
    closeReviewContentSidebar,
    resetLessonState
  } = useStudentData();

  return {
    fetchSectionContent: <T,>(data: {
      section: string;
      termId: number;
      weekId: number;
      gradeId: number;
      lessonId: number;
      subjectSlug: string;
    }) => fetchSectionContent<T>(data),

    startLesson: (data: {
      termId: number;
      weekId: number;
      gradeId: number;
      lessonId: number;
      subjectSlug: string;
    }) => startLesson(data),

    completeLesson: (data: {
      termId: number;
      weekId: number;
      gradeId: number;
      lessonId: number;
      subjectSlug: string;
    }) => completeLesson(data),

    setShowBlockNavigationModal,
    setPendingNavigationPath,
    setHasAttemptedApplyQuestion,
    setApplyResultViewShown,
    openReviewContentSidebar,
    closeReviewContentSidebar,
    resetLessonState
  };
}
